import React, { useRef, useState } from "react";
import { styled } from "styled-components";

export const PageWrapper = styled.div<{ $padding?: string }>`
  padding: ${(p) => p.$padding ?? "0 20px 100px"};
  display: flex;
  flex-direction: column;
`;

const TwoColumnOuter = styled.div<{ $padding?: string; $minWidth: string }>`
  padding: ${(p) => p.$padding ?? "0 20px 100px"};
  display: flex;
  flex-direction: column;
  row-gap: 20px;

  @media (min-width: ${(p) => p.$minWidth}) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const TwoColumnPane = styled.div<{ $minWidth: string }>`
  min-width: 0;
  flex: 1 1 auto;

  @media (min-width: ${(p) => p.$minWidth}) {
    flex: var(--pane-flex, 1) 1 0;
  }
`;

const ResizeHandle = styled.div<{ $minWidth: string }>`
  flex: 0 0 12px;
  cursor: ew-resize;
  position: relative;
  align-self: stretch;

  @media (max-width: ${(p) => p.$minWidth}) {
    display: none;
  }
`;

function useColumnRatio(
  id: string | undefined,
  defaultRatio: number
): [number, (r: number) => void] {
  const storageKey = id ? `cs_page_ratio_${id}` : null;

  const [ratio, setRatioState] = useState<number>(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        const parsed = parseFloat(parseFloat(stored).toFixed(1));
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
    return defaultRatio;
  });

  const setRatio = (r: number) => {
    setRatioState(r);
    if (storageKey) {
      localStorage.setItem(storageKey, String(r));
    }
  };

  return [ratio, setRatio];
}

interface PageWrapperTwoColumnProps {
  id?: string;
  minWidth?: string;
  padding?: string;
  defaultRatio?: number;
  children: React.ReactNode;
  className?: string;
}

export function PageWrapperTwoColumn({
  id,
  minWidth: $minWidth = "1030px",
  padding,
  defaultRatio = 0.5,
  children,
  className,
}: PageWrapperTwoColumnProps) {
  const [ratio, setRatio] = useColumnRatio(id, defaultRatio);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef<number>(0);
  const dragStartRatio = useRef<number>(0);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartRatio.current = ratio;

    const onMouseMove = (e: MouseEvent) => {
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 1;
      const usableWidth = containerWidth - 15; // subtract handle width
      const minRatio = 400 / usableWidth;
      const maxRatio = 1 - minRatio;
      const delta = e.clientX - dragStartX.current;
      const deltaRatio = delta / containerWidth;
      setRatio(
        Math.min(
          maxRatio,
          Math.max(minRatio, dragStartRatio.current + deltaRatio)
        )
      );
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const [left, right] = React.Children.toArray(children);

  return (
    <TwoColumnOuter
      ref={containerRef}
      $minWidth={$minWidth}
      $padding={padding}
      className={className}
    >
      <TwoColumnPane
        $minWidth={$minWidth}
        style={{ "--pane-flex": ratio } as React.CSSProperties}
      >
        {left}
      </TwoColumnPane>
      <ResizeHandle $minWidth={$minWidth} onMouseDown={onMouseDown} />
      <TwoColumnPane
        $minWidth={$minWidth}
        style={{ "--pane-flex": 1 - ratio } as React.CSSProperties}
      >
        {right}
      </TwoColumnPane>
    </TwoColumnOuter>
  );
}
